import { BlockType, ItemType, isPlaceableBlock, TOOL_DEFS, ToolKind } from "@craftgaming/shared";
import type { Inventory, InventoryItem } from "@craftgaming/shared";
import { getTextureAtlas } from "../rendering/texture-atlas";

// Pixel-art icons for non-block items (drawn once, cached as data URL)
const itemIconCache = new Map<number, string>();

export function getItemIcon(id: number, size: number): string {
  const key = id * 1000 + size;
  const cached = itemIconCache.get(key);
  if (cached) return cached;

  const canvas = document.createElement("canvas");
  canvas.width = 16;
  canvas.height = 16;
  const ctx = canvas.getContext("2d")!;

  const px = (x: number, y: number, c: string) => {
    ctx.fillStyle = c;
    ctx.fillRect(x, y, 1, 1);
  };

  // Tools: proper pixel-art with dark outline + shading, tinted by tier
  const tool = TOOL_DEFS[id];
  if (tool) {
    const OUT = "#232323";
    const pal =
      id >= 140 ? { main: "#35c8d6", lite: "#9af0f8" } // diamond
      : id >= 130 ? { main: "#dcdcdc", lite: "#ffffff" } // iron
      : id >= 114 ? { main: "#8f8f8f", lite: "#c4c4c4" } // stone
      : { main: "#b07a3a", lite: "#d8a860" }; // wood
    const H = "#7a5434", Hd = "#4a3420", G = "#caa03a", Gd = "#8a6a1a";

    if (tool.kind === ToolKind.SWORD) {
      // Diagonal blade, tip upper-right (classic Minecraft look)
      px(13, 0, OUT); px(12, 0, OUT);
      for (let r = 1; r <= 9; r++) {
        const c = 13 - r;
        px(c - 1, r, OUT); px(c, r, pal.main); px(c + 1, r, pal.lite); px(c + 2, r, OUT);
      }
      // Gold cross-guard
      px(3, 9, OUT); px(4, 9, G); px(5, 9, G); px(6, 9, OUT);
      px(4, 10, Gd); px(5, 10, Gd);
      // Brown handle down to the pommel
      px(3, 10, OUT); px(4, 11, OUT); px(3, 11, H); px(2, 11, OUT);
      px(2, 12, H); px(1, 12, Hd); px(3, 12, OUT);
      px(1, 13, H); px(2, 13, Hd); px(0, 13, OUT); px(1, 14, OUT);
    } else {
      // Wooden handle diagonal (lower-right → centre) for all heads
      for (let i = 0; i < 8; i++) {
        px(12 - i, 15 - i, OUT);
        px(11 - i, 15 - i, i === 0 ? OUT : H);
        px(10 - i, 15 - i, OUT);
      }
      if (tool.kind === ToolKind.PICKAXE) {
        for (let x = 3; x <= 12; x++) { px(x, 1, OUT); px(x, 3, OUT); }
        for (let x = 3; x <= 12; x++) px(x, 2, x <= 7 ? pal.lite : pal.main);
        px(2, 2, OUT); px(13, 2, OUT); px(3, 3, pal.main); px(12, 3, pal.main);
      } else if (tool.kind === ToolKind.AXE) {
        for (let x = 3; x <= 9; x++) { px(x, 1, OUT); px(x, 8, OUT); }
        for (let y = 1; y <= 8; y++) { px(3, y, OUT); px(9, y, OUT); }
        for (let y = 2; y <= 7; y++) for (let x = 4; x <= 8; x++) px(x, y, x <= 5 ? pal.lite : pal.main);
      } else {
        for (let x = 4; x <= 10; x++) { px(x, 1, OUT); px(x, 7, OUT); }
        for (let y = 1; y <= 7; y++) { px(4, y, OUT); px(10, y, OUT); }
        for (let y = 2; y <= 6; y++) for (let x = 5; x <= 9; x++) px(x, y, x <= 6 ? pal.lite : pal.main);
      }
    }
    const url = canvas.toDataURL();
    itemIconCache.set(key, url);
    return url;
  }

  switch (id) {
    case ItemType.APPLE:
      ctx.fillStyle = "#c62828";
      ctx.fillRect(4, 6, 8, 7);
      ctx.fillRect(3, 7, 10, 5);
      ctx.fillStyle = "#e53935";
      ctx.fillRect(5, 7, 3, 3);
      ctx.fillStyle = "#5d4037";
      ctx.fillRect(7, 3, 2, 3);
      ctx.fillStyle = "#43a047";
      ctx.fillRect(9, 3, 3, 2);
      break;
    case ItemType.PORKCHOP:
    case ItemType.BEEF:
      ctx.fillStyle = id === ItemType.BEEF ? "#a83232" : "#e88a9a";
      ctx.fillRect(3, 5, 10, 7);
      ctx.fillRect(4, 4, 8, 9);
      ctx.fillStyle = id === ItemType.BEEF ? "#c85252" : "#f8b0bd";
      ctx.fillRect(5, 6, 4, 3);
      ctx.fillStyle = "#f0e0d0";
      ctx.fillRect(11, 10, 3, 3); // bone
      break;
    case ItemType.CHICKEN_MEAT:
      ctx.fillStyle = "#f0c8a0";
      ctx.fillRect(4, 5, 8, 7);
      ctx.fillStyle = "#e0a878";
      ctx.fillRect(5, 6, 5, 4);
      ctx.fillStyle = "#f8f0e0";
      ctx.fillRect(10, 3, 3, 4); // drumstick bone
      break;
    case ItemType.COOKED_PORKCHOP:
    case ItemType.COOKED_BEEF:
      ctx.fillStyle = "#7a3d1a";
      ctx.fillRect(3, 5, 10, 7);
      ctx.fillRect(4, 4, 8, 9);
      ctx.fillStyle = "#9c5528";
      ctx.fillRect(5, 6, 4, 3);
      ctx.fillStyle = "#f0e0d0";
      ctx.fillRect(11, 10, 3, 3);
      break;
    case ItemType.COOKED_CHICKEN:
      ctx.fillStyle = "#c07a2a";
      ctx.fillRect(4, 5, 8, 7);
      ctx.fillStyle = "#e0a030";
      ctx.fillRect(5, 6, 5, 4);
      ctx.fillStyle = "#f8f0e0";
      ctx.fillRect(10, 3, 3, 4);
      break;
    case ItemType.STICK:
      ctx.fillStyle = "#8d6e63";
      for (let i = 0; i < 10; i++) ctx.fillRect(3 + i, 12 - i, 2, 2);
      break;
    case ItemType.COAL:
      ctx.fillStyle = "#2a2a2a";
      ctx.fillRect(4, 5, 8, 7);
      ctx.fillRect(3, 6, 10, 5);
      ctx.fillStyle = "#4a4a4a";
      ctx.fillRect(6, 6, 2, 2);
      break;
    case ItemType.IRON_INGOT:
      ctx.fillStyle = "#d8d8d8";
      ctx.fillRect(3, 6, 10, 4);
      ctx.fillStyle = "#f0f0f0";
      ctx.fillRect(4, 6, 8, 1);
      ctx.fillStyle = "#a8a8a8";
      ctx.fillRect(3, 9, 10, 1);
      break;
    case ItemType.DIAMOND_GEM:
      ctx.fillStyle = "#5ce0e8";
      ctx.fillRect(6, 3, 4, 2);
      ctx.fillRect(4, 5, 8, 4);
      ctx.fillRect(6, 9, 4, 3);
      ctx.fillStyle = "#b0f8ff";
      ctx.fillRect(6, 5, 2, 2);
      break;
    default:
      ctx.fillStyle = "#ff00ff";
      ctx.fillRect(2, 2, 12, 12);
  }

  const url = canvas.toDataURL();
  itemIconCache.set(key, url);
  return url;
}

/** Icon (data URL) for any inventory id — block or item. */
export function getSlotIcon(id: number, size: number): string {
  if (isPlaceableBlock(id)) return getTextureAtlas().getBlockPreview(id, size);
  return getItemIcon(id, size);
}

const BLOCK_COLORS: Record<number, string> = {
  [BlockType.STONE]: "#808080",
  [BlockType.DIRT]: "#8B5A2B",
  [BlockType.GRASS]: "#4DA52E",
  [BlockType.SAND]: "#E6D999",
  [BlockType.WOOD]: "#8B5A1E",
  [BlockType.PLANKS]: "#B38B4D",
  [BlockType.COBBLESTONE]: "#737373",
  [BlockType.GLASS]: "#CCE6F0",
  [BlockType.LEAVES]: "#2D8012",
  [BlockType.COAL_ORE]: "#4A4A4A",
  [BlockType.IRON_ORE]: "#B8A090",
  [BlockType.GOLD_ORE]: "#E8C840",
  [BlockType.DIAMOND_ORE]: "#5CE0E8",
};

export const BLOCK_NAMES: Record<number, string> = {
  [BlockType.STONE]: "Pedra",
  [BlockType.DIRT]: "Terra",
  [BlockType.GRASS]: "Grama",
  [BlockType.SAND]: "Areia",
  [BlockType.WOOD]: "Madeira",
  [BlockType.PLANKS]: "Tábuas",
  [BlockType.COBBLESTONE]: "Pedregulho",
  [BlockType.GLASS]: "Vidro",
  [BlockType.LEAVES]: "Folhas",
  [BlockType.COAL_ORE]: "Minério de carvão",
  [BlockType.IRON_ORE]: "Minério de ferro",
  [BlockType.GOLD_ORE]: "Minério de ouro",
  [BlockType.DIAMOND_ORE]: "Minério de diamante",
  [BlockType.CRAFTING_TABLE]: "Mesa de trabalho",
  [BlockType.BED]: "Cama",
  [BlockType.CHEST]: "Baú",
  [BlockType.FURNACE]: "Fornalha",
  [ItemType.APPLE]: "Maçã",
  [ItemType.PORKCHOP]: "Costela crua",
  [ItemType.BEEF]: "Bife cru",
  [ItemType.CHICKEN_MEAT]: "Frango cru",
  [ItemType.COOKED_PORKCHOP]: "Costela assada",
  [ItemType.COOKED_BEEF]: "Bife assado",
  [ItemType.COOKED_CHICKEN]: "Frango assado",
  [ItemType.STICK]: "Graveto",
  [ItemType.COAL]: "Carvão",
  [ItemType.IRON_INGOT]: "Lingote de ferro",
  [ItemType.DIAMOND_GEM]: "Diamante",
  [ItemType.WOODEN_PICKAXE]: "Picareta (madeira)",
  [ItemType.WOODEN_AXE]: "Machado (madeira)",
  [ItemType.WOODEN_SHOVEL]: "Pá (madeira)",
  [ItemType.WOODEN_SWORD]: "Espada (madeira)",
  [ItemType.STONE_PICKAXE]: "Picareta (pedra)",
  [ItemType.STONE_AXE]: "Machado (pedra)",
  [ItemType.STONE_SHOVEL]: "Pá (pedra)",
  [ItemType.STONE_SWORD]: "Espada (pedra)",
  [ItemType.IRON_PICKAXE]: "Picareta (ferro)",
  [ItemType.IRON_AXE]: "Machado (ferro)",
  [ItemType.IRON_SHOVEL]: "Pá (ferro)",
  [ItemType.IRON_SWORD]: "Espada (ferro)",
  [ItemType.DIAMOND_PICKAXE]: "Picareta (diamante)",
  [ItemType.DIAMOND_AXE]: "Machado (diamante)",
  [ItemType.DIAMOND_SHOVEL]: "Pá (diamante)",
  [ItemType.DIAMOND_SWORD]: "Espada (diamante)",
};

const HOTBAR_SIZE = 9;

export function getBlockColor(blockId: number): string {
  return BLOCK_COLORS[blockId] || "#ff00ff";
}

export type Hotbar = {
  selectedIndex: number;
  /** Selected slot content — block id OR item id (see items.ts). */
  getSelectedBlock: () => number;
  refresh: () => void;
  dispose: () => void;
};

export function createHotbar(
  inventory: Inventory,
  options: { isUiOpen?: () => boolean } = {}
): Hotbar {
  let selectedIndex = 0;

  const container = document.createElement("div");
  container.id = "hotbar";
  container.style.cssText = `
    position: absolute;
    bottom: 16px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 2px;
    pointer-events: auto;
    user-select: none;
  `;

  const slotElements: HTMLDivElement[] = [];
  const previewElements: HTMLDivElement[] = [];
  const countElements: HTMLSpanElement[] = [];

  for (let i = 0; i < HOTBAR_SIZE; i++) {
    const slot = document.createElement("div");
    slot.style.cssText = `
      width: 48px;
      height: 48px;
      border: 3px solid #555;
      background: #1a1a1a;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      border-radius: 4px;
    `;

    const blockPreview = document.createElement("div");
    blockPreview.style.cssText = `
      width: 34px;
      height: 34px;
      border-radius: 3px;
      display: none;
      box-shadow: inset 0 -3px 0 rgba(0,0,0,0.3), inset 0 3px 0 rgba(255,255,255,0.1);
    `;

    const countLabel = document.createElement("span");
    countLabel.style.cssText = `
      position: absolute;
      bottom: 1px;
      right: 3px;
      font-size: 10px;
      font-weight: bold;
      color: #fff;
      font-family: monospace;
      text-shadow: 0 1px 2px rgba(0,0,0,0.9);
      display: none;
    `;

    slot.appendChild(blockPreview);
    slot.appendChild(countLabel);
    container.appendChild(slot);
    slotElements.push(slot);
    previewElements.push(blockPreview);
    countElements.push(countLabel);

    // Tap/click selects the slot (mobile + mouse)
    const slotIndex = i;
    slot.addEventListener("pointerdown", (e) => {
      e.stopPropagation();
      selectedIndex = slotIndex;
      updateSelection();
    });
  }

  const overlay = document.getElementById("ui-overlay");
  if (overlay) overlay.appendChild(container);

  refresh();
  updateSelection();

  function handleKeyDown(e: KeyboardEvent) {
    if (options.isUiOpen?.()) return; // typing/menus must not switch slots
    const digit = parseInt(e.code.replace("Digit", ""), 10);
    if (digit >= 5 && digit <= 9) {
      selectedIndex = digit - 5 + 4;
      updateSelection();
    }
  }

  function handleWheel(e: WheelEvent) {
    if (options.isUiOpen?.()) return; // let overlays scroll normally
    e.preventDefault();
    const dir = e.deltaY > 0 ? 1 : -1;
    selectedIndex = ((selectedIndex + dir) % HOTBAR_SIZE + HOTBAR_SIZE) % HOTBAR_SIZE;
    updateSelection();
  }

  function updateSelection() {
    for (let i = 0; i < slotElements.length; i++) {
      if (i === selectedIndex) {
        slotElements[i].style.borderColor = "#fff";
        slotElements[i].style.transform = "scale(1.1)";
      } else {
        slotElements[i].style.borderColor = "#555";
        slotElements[i].style.transform = "scale(1)";
      }
    }
  }

  function refresh() {
    for (let i = 0; i < HOTBAR_SIZE; i++) {
      const item: InventoryItem | null = inventory.items[i];
      if (item && item.count > 0) {
        previewElements[i].style.display = "block";
        previewElements[i].style.background = `url(${getSlotIcon(item.blockId, 34)}) center/cover`;
        previewElements[i].style.imageRendering = "pixelated";
        countElements[i].style.display = "block";
        countElements[i].textContent = item.count > 1 ? String(item.count) : "";
      } else {
        previewElements[i].style.display = "none";
        previewElements[i].style.background = "";
        countElements[i].style.display = "none";
      }
    }
  }

  document.addEventListener("keydown", handleKeyDown);
  document.addEventListener("wheel", handleWheel, { passive: false });

  function dispose() {
    document.removeEventListener("keydown", handleKeyDown);
    document.removeEventListener("wheel", handleWheel);
    container.remove();
  }

  return {
    get selectedIndex() { return selectedIndex; },
    set selectedIndex(v: number) { selectedIndex = v; updateSelection(); },
    getSelectedBlock() {
      const item = inventory.items[selectedIndex];
      return item ? item.blockId : BlockType.AIR;
    },
    refresh,
    dispose,
  };
}
